import * as Sentry from "@sentry/react";

import {
  LDContext,
  LDProvider,
  basicLogger,
} from "launchdarkly-react-client-sdk";

import useTeam from "../../react-query/queries/useTeam";
import { LDMultiKindContext } from "launchdarkly-js-sdk-common";
import React, { useEffect, useState } from "react";
import useCurrentUser from "../../react-query/queries/useCurrentUser";
import { IS_DEVELOPMENT } from "@util/deployEnv.ts";
import { LAUNCHDARKLY_CLIENT_SIDE_ID } from "@util/settings.ts";

const logger = IS_DEVELOPMENT
  ? basicLogger({
      // suppresses info/debug level logging from LD
      level: "warn",
    })
  : basicLogger({
      level: "error",
      destination: (line) => {
        if (!line.includes("network error")) {
          Sentry.captureException(new Error(line), { level: "error" });
        }
      },
    });

interface LaunchDarklyProviderProps {
  children: React.ReactNode;
  isPublic?: boolean;
}

const LaunchDarklyProvider = ({
  children,
  isPublic,
}: LaunchDarklyProviderProps) => {
  const { data: user } = useCurrentUser();
  const doesUserHaveTeam = !!user?.team_id;
  const { data: team, isLoading: teamIsLoading } = useTeam({
    enabled: doesUserHaveTeam,
  });
  const [context, setContext] = useState<LDContext>();

  useEffect(() => {
    if (isPublic) {
      // TODO implement session based anonymous users. Currently all anonymous users are treated as the same user.
      setContext({
        kind: "user",
        anonymous: true,
      });
      return;
    }

    if (!user || (doesUserHaveTeam && teamIsLoading)) {
      return;
    }

    const context: LDMultiKindContext = {
      kind: "multi",
      user: {
        kind: "user",
        key: user.email,
      },
    };

    if (team) {
      context.team = {
        kind: "team",
        key: team.id.toString(),
        name: team.name,
        teamId: team.id,
      };
    }

    setContext(context);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, team?.id, teamIsLoading, doesUserHaveTeam, isPublic]);

  return (
    <LDProvider
      clientSideID={LAUNCHDARKLY_CLIENT_SIDE_ID}
      // Defer initialization until we have a user to avoid LD Context switching causing flag flicker (https://github.com/launchdarkly/react-client-sdk/issues/130#issuecomment-1756735037)
      deferInitialization
      context={context}
      reactOptions={{
        useCamelCaseFlagKeys: false,
      }}
      options={{
        logger,
      }}
    >
      {children}
    </LDProvider>
  );
};

export default LaunchDarklyProvider;
